import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const WEBIDEAL_LOGO = '/logofinal.png';

const BackChevron = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
    <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" />
  </svg>
);

const LogoMark = () => (
  <motion.div
    className="flex items-center gap-3"
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <img
      src={WEBIDEAL_LOGO}
      alt="WebIdeal ATS"
      className="h-14 w-auto object-contain rounded-xl"
    />
    
  </motion.div>
);

const LeftSpotlightNetwork = () => (
  <>
    <motion.div
      className="absolute inset-0"
      animate={{
        background: [
          'radial-gradient(circle at 18% 22%, rgba(251,146,60,0.16), transparent 24%)',
          'radial-gradient(circle at 24% 28%, rgba(251,146,60,0.12), transparent 26%)',
          'radial-gradient(circle at 18% 22%, rgba(251,146,60,0.16), transparent 24%)',
        ],
      }}
      transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
    />

    <motion.div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      animate={{ opacity: [0.12, 0.22, 0.12] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
    >
      <svg className="w-full h-full" viewBox="0 0 900 1000" fill="none" preserveAspectRatio="xMidYMid slice">
        <g stroke="#fdba74" strokeWidth="1.2">
          <path d="M-30 170 C 120 130, 210 255, 340 205 S 570 145, 760 220" />
          <path d="M-40 360 C 140 300, 250 430, 410 390 S 650 340, 850 420" />
          <path d="M-20 610 C 160 560, 300 670, 470 625 S 710 575, 920 660" />
        </g>

        {[
          { cx: 110, cy: 158, r: 4.5, delay: 0.1 },
          { cx: 250, cy: 225, r: 4.5, delay: 0.35 },
          { cx: 390, cy: 195, r: 4.5, delay: 0.7 },
          { cx: 610, cy: 185, r: 4.5, delay: 1.0 },
          { cx: 180, cy: 350, r: 4.5, delay: 1.3 },
          { cx: 345, cy: 392, r: 4.5, delay: 1.6 },
          { cx: 585, cy: 360, r: 4.5, delay: 1.9 },
          { cx: 235, cy: 595, r: 4.5, delay: 2.2 },
          { cx: 465, cy: 625, r: 4.5, delay: 2.5 },
          { cx: 720, cy: 610, r: 4.5, delay: 2.8 },
        ].map((n, i) => (
          <motion.circle
            key={i}
            cx={n.cx}
            cy={n.cy}
            r={n.r}
            fill="#fb923c"
            animate={{ opacity: [0.28, 0.95, 0.28], scale: [1, 1.4, 1] }}
            transition={{ duration: 3.4, delay: n.delay, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </svg>
    </motion.div>

    <motion.div
      className="absolute top-16 -left-10 w-56 h-56 rounded-full bg-orange-200 blur-3xl opacity-20"
      animate={{ x: [0, 12, 0], y: [0, -12, 0], scale: [1, 1.04, 1] }}
      transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
    />
  </>
);

const ScreenWrapper = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 22 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.55, ease: 'easeOut' }}
    className="w-full max-w-md relative z-10"
  >
    {children}
  </motion.div>
);

const FloatInput = ({ label, type = 'text', value, onChange, disabled }) => {
  const [focused, setFocused] = useState(false);
  const filled = value && value.length > 0;
  const lifted = focused || filled;

  return (
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        disabled={disabled}
        placeholder=""
        className="w-full px-4 pt-5 pb-2 rounded-2xl outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed bg-white"
        style={{
          border: `1px solid ${focused ? '#f97316' : '#e2e8f0'}`,
          color: '#0f172a',
          boxShadow: focused ? '0 0 0 4px rgba(249,115,22,0.12)' : 'none',
        }}
      />
      <label
        style={{
          position: 'absolute',
          left: '1rem',
          top: lifted ? '0.35rem' : '0.85rem',
          fontSize: lifted ? '0.7rem' : '0.875rem',
          color: focused ? '#f97316' : '#64748b',
          transition: 'all 0.2s ease',
          pointerEvents: 'none',
          fontWeight: lifted ? 600 : 400,
        }}
      >
        {label}
      </label>
    </div>
  );
};

const FloatSelect = ({ label, value, onChange, disabled, options }) => {
  const [focused, setFocused] = useState(false);

  return (
    <div className="relative">
      <label
        style={{
          position: 'absolute',
          left: '1rem',
          top: '0.35rem',
          fontSize: '0.7rem',
          color: focused ? '#f97316' : '#64748b',
          pointerEvents: 'none',
          fontWeight: 600,
          zIndex: 1,
          transition: 'color 0.2s ease',
        }}
      >
        {label}
      </label>

      <select
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        disabled={disabled}
        className="w-full px-4 pt-6 pb-2 rounded-2xl outline-none transition-all duration-200 disabled:opacity-50 appearance-none bg-white"
        style={{
          border: `1px solid ${focused ? '#f97316' : value ? '#f97316' : '#e2e8f0'}`,
          color: value ? '#0f172a' : '#94a3b8',
          boxShadow: focused ? '0 0 0 4px rgba(249,115,22,0.12)' : 'none',
        }}
      >
        <option value="" disabled>
          Select a plan…
        </option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      <span
        style={{
          position: 'absolute',
          right: '0.9rem',
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          color: '#94a3b8',
        }}
      >
        <svg viewBox="0 0 20 20" fill="currentColor" style={{ width: '1rem', height: '1rem' }}>
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </span>
    </div>
  );
};

const ShimmerButton = ({ onClick, disabled, loading, children, type = 'button', outline = false }) => (
  <motion.button
    whileHover={disabled ? {} : { y: -2, scale: 1.01 }}
    whileTap={disabled ? {} : { scale: 0.99 }}
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`
      relative w-full py-3.5 rounded-2xl font-bold text-base transition-all duration-200
      overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed
      ${outline
        ? 'border-2 text-orange-600 border-orange-500 bg-white hover:bg-orange-50'
        : 'text-white shadow-lg'}
    `}
    style={
      outline
        ? {}
        : {
            background: 'linear-gradient(135deg, #f97316 0%, #fb923c 55%, #f59e0b 100%)',
            boxShadow: '0 10px 24px rgba(249,115,22,0.28)',
          }
    }
  >
    {!outline && !disabled && (
      <span
        className="shimmer-sweep"
        style={{
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '60%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent)',
          animation: 'shimmer 2.5s infinite',
        }}
      />
    )}
    <span className="relative z-10">{loading ? `${String(children).split(' ')[0]}ing...` : children}</span>
  </motion.button>
);

const ErrorBox = ({ message }) =>
  message ? (
    <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-2xl px-4 py-3" style={{ animation: 'fadeInUp 0.25s ease' }}>
      {message}
    </div>
  ) : null;

const SuccessBox = ({ message }) =>
  message ? (
    <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-2xl px-4 py-3">
      {message}
    </div>
  ) : null;

const RightPanel = () => (
  <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
    <motion.div
      className="absolute inset-0"
      animate={{
        background: [
          'linear-gradient(135deg, #fb923c 0%, #f97316 45%, #f59e0b 100%)',
          'linear-gradient(135deg, #fdba74 0%, #f97316 50%, #fb923c 100%)',
          'linear-gradient(135deg, #fb923c 0%, #f97316 45%, #f59e0b 100%)',
        ],
      }}
      transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
    />

    <motion.div
      className="absolute inset-0"
      animate={{
        background: [
          'radial-gradient(circle at 72% 28%, rgba(255,255,255,0.30), transparent 28%)',
          'radial-gradient(circle at 68% 32%, rgba(255,255,255,0.24), transparent 30%)',
          'radial-gradient(circle at 72% 28%, rgba(255,255,255,0.30), transparent 28%)',
        ],
      }}
      transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
    />

    <div className="absolute inset-0 pointer-events-none opacity-30">
      <svg className="w-full h-full" viewBox="0 0 1000 1000" fill="none" preserveAspectRatio="xMidYMid slice">
        <motion.path
          d="M-50 180 C 120 120, 240 280, 420 220 S 760 120, 1050 220"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          initial={{ pathLength: 0.7, opacity: 0.25 }}
          animate={{ pathLength: [0.7, 1, 0.7], opacity: [0.2, 0.45, 0.2] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.path
          d="M-80 340 C 140 260, 260 420, 440 360 S 760 260, 1080 390"
          stroke="white"
          strokeWidth="1.8"
          strokeLinecap="round"
          initial={{ pathLength: 0.65, opacity: 0.2 }}
          animate={{ pathLength: [0.65, 1, 0.65], opacity: [0.15, 0.38, 0.15] }}
          transition={{ duration: 7.5, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.path
          d="M-100 560 C 120 500, 260 640, 470 570 S 820 500, 1100 620"
          stroke="white"
          strokeWidth="1.8"
          strokeLinecap="round"
          initial={{ pathLength: 0.6, opacity: 0.18 }}
          animate={{ pathLength: [0.6, 1, 0.6], opacity: [0.12, 0.32, 0.12] }}
          transition={{ duration: 8.5, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.path
          d="M-40 760 C 160 700, 280 860, 500 790 S 800 700, 1080 820"
          stroke="white"
          strokeWidth="1.6"
          strokeLinecap="round"
          initial={{ pathLength: 0.55, opacity: 0.14 }}
          animate={{ pathLength: [0.55, 1, 0.55], opacity: [0.1, 0.26, 0.1] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        />

        {[
          { cx: 180, cy: 168, delay: 0.1 },
          { cx: 355, cy: 227, delay: 0.4 },
          { cx: 690, cy: 185, delay: 0.8 },
          { cx: 240, cy: 330, delay: 1.1 },
          { cx: 590, cy: 360, delay: 1.4 },
          { cx: 790, cy: 545, delay: 1.8 },
          { cx: 430, cy: 575, delay: 2.2 },
          { cx: 700, cy: 780, delay: 2.6 },
        ].map((n, i) => (
          <motion.circle
            key={i}
            cx={n.cx}
            cy={n.cy}
            r="4"
            fill="white"
            animate={{ opacity: [0.25, 0.95, 0.25], scale: [1, 1.4, 1] }}
            transition={{ duration: 3.5, delay: n.delay, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </svg>
    </div>

    <motion.div
      className="absolute -top-10 right-0 w-72 h-72 rounded-full bg-white blur-3xl opacity-20"
      animate={{ x: [0, -20, 0], y: [0, 20, 0], scale: [1, 1.05, 1] }}
      transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      className="absolute bottom-10 left-8 w-64 h-64 rounded-full bg-white blur-3xl opacity-15"
      animate={{ x: [0, 16, 0], y: [0, -16, 0], scale: [1, 1.04, 1] }}
      transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
    />

    <div className="relative z-10 w-full flex flex-col justify-between p-10 xl:p-14 text-white">
      <motion.div
        className="flex items-center gap-3"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div>
          <img src={WEBIDEAL_LOGO} alt="WebIdeal ATS" className="h-10 w-auto object-contain rounded-lg" />
        </div>
        <span className="text-lg font-semibold tracking-tight">WebIdeal ATS</span>
      </motion.div>

      <div className="max-w-xl">
        <motion.p
          className="text-sm uppercase tracking-[0.22em] text-orange-100/90 mb-4"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.1 }}
        >
          Talent Operations Platform
        </motion.p>

        <motion.h1
          className="text-5xl xl:text-6xl font-extrabold leading-tight mb-6"
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          Hire Faster.
          <br />
          Track Smarter.
        </motion.h1>

        <motion.p
          className="text-lg text-orange-50/90 max-w-lg leading-8"
          initial={{ opacity: 0, y: 26 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
        >
          Move candidates through sourcing, screening, interviews, and offers with one intelligent ATS workspace.
        </motion.p>

        <div className="grid grid-cols-2 gap-4 mt-10 max-w-lg">
          {[
            { title: 'Applicants Reviewed', value: '184', sub: 'This hiring cycle', floatY: 6 },
            { title: 'Interviews Scheduled', value: '26', sub: 'Across active roles', floatY: 9 },
            { title: 'Offers Pending', value: '07', sub: 'Awaiting approval', floatY: 7 },
            { title: 'Pipeline Health', value: '92%', sub: 'Strong conversion rate', floatY: 10 },
          ].map((card, i) => (
            <motion.div
              key={card.title}
              className="rounded-3xl bg-white/14 backdrop-blur-md p-5 border border-white/25 shadow-lg"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.3 + i * 0.08 }}
              whileHover={{ y: -5, scale: 1.02 }}
            >
              <motion.div
                animate={{ y: [0, -card.floatY, 0] }}
                transition={{ duration: 4.5 + i * 0.5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <p className="text-sm text-orange-50/85">{card.title}</p>
                <h3 className="text-3xl font-bold mt-1">{card.value}</h3>
                <p className="text-xs mt-2 text-orange-100/80">{card.sub}</p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.div
        className="text-sm text-orange-50/90"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.5 }}
      >
        Trusted ATS platform for recruiters, hiring teams, and enterprise talent operations.
      </motion.div>
    </div>
  </div>
);

const LoginScreen = ({ onLoginSuccess, onForgotPassword, onRequestAccess }) => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSignIn = async () => {
    setError('');
    if (!email.trim()) return setError('Email is required.');
    if (!password) return setError('Password is required.');

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Login failed.');

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Update AuthContext to sync authentication state
      login(data.user);
      
      onLoginSuccess(data);
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScreenWrapper>
      <div className="lg:hidden mb-6">
        <LogoMark />
      </div>

      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.05 }}
      >
        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-2">Welcome back</h2>
        <p className="text-slate-500">Sign in to your ATS workspace</p>
      </motion.div>

      <motion.form
        onSubmit={(e) => {
          e.preventDefault();
          handleSignIn();
        }}
        className="space-y-4"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.12 }}
      >
        <FloatInput label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} />
        <FloatInput label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} />

        <div className="flex justify-between items-center text-sm pt-1">
          <label className="flex items-center gap-2 text-slate-500">
            <input type="checkbox" className="accent-orange-500 rounded" />
            Remember me
          </label>
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-orange-600 hover:text-orange-700 font-semibold transition hover:underline"
          >
            Forgot Password?
          </button>
        </div>

        <ErrorBox message={error} />
        <ShimmerButton type="submit" disabled={loading} loading={loading}>
          Sign In
        </ShimmerButton>
      </motion.form>

      <motion.div
        className="flex flex-col items-center mt-6 space-y-4"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, delay: 0.18 }}
      >
        <div className="flex items-center w-full gap-3 py-1">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-xs font-medium tracking-wide text-slate-400">New to WebIdeal?</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        <ShimmerButton outline onClick={onRequestAccess} disabled={false} loading={false}>
          Start Your Hiring Engine
        </ShimmerButton>

        <button type="button" className="text-orange-600 text-sm hover:underline transition font-medium">
          Trouble Logging In?
        </button>
      </motion.div>
    </ScreenWrapper>
  );
};

const ForgotPasswordScreen = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async () => {
    if (!email.trim()) return setError('Email is required.');
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Request failed.');
      setSuccess(data.message || 'If this email exists, a reset link has been sent.');
    } catch (err) {
      setError(err.message || 'Request failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScreenWrapper>
      <button onClick={onBack} className="flex items-center text-slate-500 hover:text-slate-700 mb-6 text-sm font-medium transition">
        <BackChevron /> Back to Sign In
      </button>

      <h2 className="text-3xl font-extrabold text-slate-900 mb-2">Reset Password</h2>
      <p className="text-slate-500 mb-6 text-sm">Enter your email to receive a reset link.</p>

      <div className="space-y-4">
        <FloatInput label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading || !!success} />
        <ErrorBox message={error} />
        <SuccessBox message={success} />

        {!success && (
          <ShimmerButton onClick={handleSubmit} disabled={loading} loading={loading}>
            Send Reset Link
          </ShimmerButton>
        )}

        {success && (
          <button
            onClick={onBack}
            className="w-full text-orange-600 py-3 font-semibold rounded-2xl transition border-2 border-orange-300 bg-orange-50 hover:bg-orange-100"
          >
            Back to Sign In
          </button>
        )}
      </div>
    </ScreenWrapper>
  );
};

const ResetPasswordScreen = ({ resetToken, onResetComplete }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleReset = async () => {
    if (!password) return setError('Password is required.');
    if (password.length < 8) return setError('Minimum 8 characters.');
    if (password !== confirm) return setError('Passwords do not match.');

    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Reset failed.');
      setSuccess(true);
      setTimeout(() => onResetComplete(), 2000);
    } catch (err) {
      setError(err.message || 'Reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <ScreenWrapper>
        <div className="text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4" style={{ animation: 'popIn 0.4s ease' }}>
            <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Password Reset!</h2>
          <p className="text-slate-500 text-sm">Redirecting you to sign in...</p>
        </div>
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper>
      <h2 className="text-3xl font-extrabold text-slate-900 mb-2">Set New Password</h2>
      <p className="text-slate-500 mb-6 text-sm">Enter your new password below.</p>

      <div className="space-y-4">
        <FloatInput label="New Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} />
        <FloatInput label="Confirm Password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={loading} />
        <ErrorBox message={error} />
        <ShimmerButton onClick={handleReset} disabled={loading} loading={loading}>
          Reset Password
        </ShimmerButton>
      </div>
    </ScreenWrapper>
  );
};

const SetupPasswordScreen = ({ setupToken, onSetupComplete }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSetup = async () => {
    if (!password) return setError('Password is required.');
    if (password.length < 8) return setError('Minimum 8 characters.');
    if (password !== confirm) return setError('Passwords do not match.');

    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: setupToken, password }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Setup failed.');
      setSuccess(true);
      setTimeout(() => onSetupComplete(), 2000);
    } catch (err) {
      setError(err.message || 'Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <ScreenWrapper>
        <div className="text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4" style={{ animation: 'popIn 0.4s ease' }}>
            <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Password Set!</h2>
          <p className="text-slate-500 text-sm">Redirecting you to sign in...</p>
        </div>
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper>
      <h2 className="text-3xl font-extrabold text-slate-900 mb-2">Set Your Password</h2>
      <p className="text-slate-500 mb-6 text-sm">Create a password to activate your account.</p>

      <div className="space-y-4">
        <FloatInput label="New Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} />
        <FloatInput label="Confirm Password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={loading} />
        <ErrorBox message={error} />
        <ShimmerButton onClick={handleSetup} disabled={loading} loading={loading}>
          Activate Account
        </ShimmerButton>
      </div>
    </ScreenWrapper>
  );
};

const RequestAccessScreen = ({ onBack }) => {
  const [form, setForm] = useState({
    contactName: '',
    companyEmail: '',
    companyName: '',
    phone: '',
    notes: '',
    requestedPlan: '',
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async () => {
    if (!form.contactName.trim()) return setError('Name is required.');
    if (!form.companyEmail.trim()) return setError('Company email is required.');
    if (!form.companyName.trim()) return setError('Company name is required.');
    if (!form.phone.trim()) return setError('Phone is required.');
    if (!form.requestedPlan) return setError('Please select a plan.');

    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/company-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: form.companyName.trim(),
          contactName: form.contactName.trim(),
          contactEmail: form.companyEmail.trim(),
          phone: form.phone.trim(),
          requestedPlan: form.requestedPlan,
          notes: form.notes.trim(),
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Submission failed.');
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <ScreenWrapper>
        <div className="text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4" style={{ animation: 'popIn 0.4s ease' }}>
            <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Request Submitted!</h2>
          <p className="text-slate-500 mb-6 text-sm">Our team will review your request and get back to you shortly.</p>
          <button onClick={onBack} className="text-orange-600 font-medium hover:underline text-sm">
            Back to Sign In
          </button>
        </div>
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper>
      <button onClick={onBack} className="flex items-center text-slate-500 hover:text-slate-700 mb-6 text-sm font-medium transition">
        <BackChevron /> Back to Sign In
      </button>

      <h2 className="text-3xl font-extrabold text-slate-900 mb-1">Start Your Hiring Engine</h2>
      <p className="text-slate-500 mb-6 text-sm">Fill in your details and we&apos;ll get you set up.</p>

      <div className="space-y-4">
        <FloatInput label="Your Name *" value={form.contactName} onChange={handleChange('contactName')} disabled={loading} />
        <FloatInput label="Company Email *" type="email" value={form.companyEmail} onChange={handleChange('companyEmail')} disabled={loading} />
        <FloatInput label="Company Name *" value={form.companyName} onChange={handleChange('companyName')} disabled={loading} />
        <FloatInput label="Phone *" type="tel" value={form.phone} onChange={handleChange('phone')} disabled={loading} />

        <FloatSelect
          label="Requested Plan *"
          value={form.requestedPlan}
          onChange={handleChange('requestedPlan')}
          disabled={loading}
          options={[
            { value: 'Starter', label: 'Starter' },
            { value: 'Growth', label: 'Growth' },
            { value: 'Enterprise', label: 'Enterprise' },
          ]}
        />

        <textarea
          placeholder="Notes (optional)"
          value={form.notes}
          onChange={handleChange('notes')}
          disabled={loading}
          rows={3}
          className="w-full p-4 rounded-2xl outline-none transition duration-200 resize-none disabled:opacity-50 bg-white"
          style={{ border: '1px solid #e2e8f0', color: '#0f172a' }}
        />

        <ErrorBox message={error} />
        <ShimmerButton onClick={handleSubmit} disabled={loading} loading={loading}>
          Submit Request
        </ShimmerButton>
      </div>
    </ScreenWrapper>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [screen, setScreen] = useState('login');
  const [setupToken, setSetupToken] = useState('');
  const [resetToken, setResetToken] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get('action');
    const token = params.get('token');

    if (action === 'setup' && token) {
      setSetupToken(token);
      setScreen('setup');
    }

    if (action === 'reset' && token) {
      setResetToken(token);
      setScreen('reset');
    }
  }, [location.search]);

  const handleLoginSuccess = () => navigate('/ats/dashboard');
  const handleSetupComplete = () => {
    navigate('/ats/login', { replace: true });
    setScreen('login');
  };
  const handleResetComplete = () => {
    navigate('/ats/login', { replace: true });
    setScreen('login');
  };

  return (
    <div className="min-h-screen bg-[#fff7ed]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');
        body { font-family: 'Inter', sans-serif; }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shimmer { 0% { left: -100%; } 100% { left: 200%; } }
        @keyframes popIn { 0% { transform: scale(0.5); opacity: 0; } 70% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
        .shimmer-sweep { animation: shimmer 2.5s infinite; }
      `}</style>

      <div className="min-h-screen flex">
        <div className="w-full lg:w-1/2 bg-white relative flex items-start justify-center px-6 sm:px-10 lg:px-14 xl:px-20 py-8 lg:py-10 overflow-y-auto">
          <LeftSpotlightNetwork />

          <motion.div
            className="w-full max-w-md relative z-10"
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55 }}
          >
            <div className="hidden lg:block mb-3">
              <LogoMark />
            </div>

            {screen === 'login' && (
              <LoginScreen
                onLoginSuccess={handleLoginSuccess}
                onForgotPassword={() => setScreen('forgot')}
                onRequestAccess={() => setScreen('request')}
              />
            )}

            {screen === 'forgot' && <ForgotPasswordScreen onBack={() => setScreen('login')} />}
            {screen === 'reset' && <ResetPasswordScreen resetToken={resetToken} onResetComplete={handleResetComplete} />}
            {screen === 'setup' && <SetupPasswordScreen setupToken={setupToken} onSetupComplete={handleSetupComplete} />}
            {screen === 'request' && <RequestAccessScreen onBack={() => setScreen('login')} />}
          </motion.div>
        </div>

        <RightPanel />
      </div>
    </div>
  );
};

export default Login;